import type { CSSProperties } from 'vue'

import type { BaseElementInput, RGBColor, TransformSpec } from '../types'
import { clamp01, clampColor, formatPercent } from './math'

export const toRgb = (color: RGBColor) =>
  `rgb(${clampColor(color.r)}, ${clampColor(color.g)}, ${clampColor(color.b)})`

export const invertColor = (color: RGBColor): RGBColor => ({
  r: 255 - clampColor(color.r),
  g: 255 - clampColor(color.g),
  b: 255 - clampColor(color.b),
})

export const buildTransform = (spec?: TransformSpec) => {
  if (!spec) {
    return undefined
  }

  const operations: string[] = []

  if (spec.mirrored) {
    operations.push('scaleX(-1)')
    operations.push('translateX(100%)')
  }

  if (typeof spec.rotation === 'number') {
    operations.push(`rotate(${spec.rotation}deg)`)
  }

  if (typeof spec.skew?.x === 'number') {
    operations.push(`skewX(${spec.skew.x}deg)`)
  }

  if (typeof spec.skew?.y === 'number') {
    operations.push(`skewY(${spec.skew.y}deg)`)
  }

  return operations.length ? operations.reverse().join(' ') : undefined
}

export const applyCommonElementStyle = (style: CSSProperties, input: BaseElementInput) => {
  style.zIndex = input.layer ?? 0
  style.transformOrigin = 'top left'
  style.opacity =
    typeof input.transforms?.opacity === 'number' ? clamp01(input.transforms.opacity) : 1
  const transformValue = buildTransform(input.transforms)
  if (transformValue) {
    style.transform = transformValue
  }
}

export const buildBaseStyle = (input: BaseElementInput): CSSProperties | null => {
  if (
    !input.position ||
    typeof input.position.x !== 'number' ||
    typeof input.position.y !== 'number'
  ) {
    return null
  }

  const style: CSSProperties = {
    position: 'absolute',
    left: formatPercent(input.position.x),
    top: formatPercent(input.position.y),
  }

  applyCommonElementStyle(style, input)
  return style
}

export const buildFullScreenStyle = (input: BaseElementInput): CSSProperties => {
  const style: CSSProperties = {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
  }

  applyCommonElementStyle(style, input)
  return style
}
